import { COLLOQUIAL_INSURANCE_NAMES } from "@repo/services/models/insuranceConstants";
import { Button, Flex, Icon, Input, Select, Text, WithTooltip } from "@repo/ui";
import { useTheme } from "@repo/ui";
import { useStyles } from "purse-styles";
import { toast } from "sonner";
import type {
	Location,
	LocationId,
	Organization,
	OrganizationId,
	Provider,
} from "../../types";
import { buildReferralText, getProviderFullName } from "./utils";

type ProviderRowProps = {
	provider: Provider;
	isExpanded: boolean;
	isEditing: boolean;
	isSaving?: boolean;
	expandedOrgIds: OrganizationId[];
	validationErrors: Record<string, string>;
	onToggleExpand: () => void;
	onStartEdit: () => void;
	onSaveEdit: () => void;
	onCancelEdit: () => void;
	onToggleOrgExpand: (orgId: OrganizationId) => void;
	onOrgChange: (
		orgId: OrganizationId,
		field: keyof Organization,
		value: string | string[],
	) => void;
	onLocationChange: (
		orgId: OrganizationId,
		locId: LocationId,
		field: keyof Location,
		value: string,
	) => void;
	onRemoveOrg: (orgId: OrganizationId) => void;
	onRemoveLocation: (orgId: OrganizationId, locId: LocationId) => void;
};

export function ProviderRow({
	provider,
	isExpanded: _isExpanded,
	isEditing,
	isSaving = false,
	expandedOrgIds: _expandedOrgIds,
	validationErrors,
	onToggleExpand: _onToggleExpand,
	onStartEdit,
	onSaveEdit,
	onCancelEdit,
	onToggleOrgExpand: _onToggleOrgExpand,
	onOrgChange,
	onLocationChange,
	onRemoveOrg,
	onRemoveLocation,
}: ProviderRowProps) {
	const theme = useTheme();
	const fullName = getProviderFullName(provider);

	const rowClassName = useStyles({
		borderBottom: `1px solid ${theme.colors.sand5}`,
		"&:last-child": {
			borderBottom: "none",
		},
	});

	const locationRowClassName = useStyles({
		borderBottom: `1px solid ${theme.colors.sand3}`,
		"&:last-child": {
			borderBottom: "none",
		},
		"&:hover": {
			background: theme.colors.sand2,
		},
	});

	const orgSeparatorClassName = useStyles({
		borderTop: `1px solid ${theme.colors.sand4}`,
		marginTop: theme.spacing.xs,
		paddingTop: theme.spacing.xs,
	});

	const editInputWrapperClassName = useStyles({
		borderBottom: `1px solid ${theme.colors.sand6}`,
		"&:focus-within": {
			borderBottomColor: theme.colors.accent9,
		},
	});

	const handleCopyLocation = async (org: Organization, loc: Location) => {
		const text = buildReferralText({
			provider,
			organization: org,
			location: loc,
		});
		try {
			await navigator.clipboard.writeText(text);
			toast.success("Copied to clipboard");
		} catch {
			toast.error("Unable to copy");
		}
	};

	return (
		<Flex column className={rowClassName}>
			{provider.organizations.map((org, orgIndex) => {
				const canRemoveOrg = provider.organizations.length > 1;
				const orgNameError = validationErrors[`org:${org.id}:companyName`];
				const isFirstOrg = orgIndex === 0;

				return (
					<Flex
						key={org.id}
						column
						className={!isFirstOrg ? orgSeparatorClassName : undefined}
					>
						{isFirstOrg && (
							<Flex
								paddingHorizontal="sm"
								paddingVertical="xs"
								align="center"
								justify="space-between"
							>
								<Flex gap="md" align="center">
									<Icon name="CircleUser" size="sm" color="sand9" />
									<Text size="md" weight="semibold">
										{fullName}
									</Text>
									{provider.specialty && (
										<Flex
											paddingHorizontal="xs"
											paddingVertical="xxs"
											background="accent3"
											borderRadius="sm"
										>
											<Text size="sm" color="accent11">
												{provider.specialty}
											</Text>
										</Flex>
									)}
									{provider.npi && (
										<Text size="sm" color="sand10">
											NPI: {provider.npi}
										</Text>
									)}
								</Flex>
								<Flex gap="xs" align="center" shrink={0}>
									{!isEditing ? (
										<Button variant="ghost" size="xs" onPress={onStartEdit}>
											<Icon name="SquarePen" size="xs" color="accent10" />
										</Button>
									) : (
										<>
											<Button
												variant="ghost"
												size="xs"
												onPress={isSaving ? undefined : onCancelEdit}
											>
												<Flex gap="xxs" align="center">
													<Icon
														name="X"
														size="xs"
														color={isSaving ? "sand8" : "red10"}
													/>
													<Text size="sm" color={isSaving ? "sand8" : "red10"}>
														Cancel
													</Text>
												</Flex>
											</Button>
											<Button
												variant="ghost"
												size="xs"
												onPress={isSaving ? undefined : onSaveEdit}
											>
												<Flex gap="xxs" align="center">
													<Icon
														name={isSaving ? "Clock" : "Check"}
														size="xs"
														color={isSaving ? "sand8" : "green10"}
													/>
													<Text
														size="xs"
														color={isSaving ? "sand8" : "green10"}
													>
														{isSaving ? "Saving..." : "Save"}
													</Text>
												</Flex>
											</Button>
										</>
									)}
								</Flex>
							</Flex>
						)}
						<Flex
							paddingHorizontal="sm"
							paddingVertical="xxs"
							align="center"
							justify="space-between"
							gap="md"
						>
							<Flex gap="md" align="center" style={{ flex: 1 }}>
								{isEditing ? (
									<div
										className={editInputWrapperClassName}
										style={{ display: "inline-flex", minWidth: 160 }}
									>
										<Input
											size="sm"
											value={org.companyName}
											onChange={(e) => {
												onOrgChange(org.id, "companyName", e.target.value);
											}}
											style={{
												width: `${Math.max(org.companyName.length, 18)}ch`,
											}}
										/>
									</div>
								) : (
									<Text size="sm" weight="medium" color="sand12">
										{org.companyName}
									</Text>
								)}
								{orgNameError && (
									<Text size="sm" color="red11">
										{orgNameError}
									</Text>
								)}
								{/* Insurance display (non-editing mode) */}
								{!isEditing &&
									(org.insuranceGroupsAccepted?.length ?? 0) > 0 && (
										<Flex gap="xs" align="center">
											<Icon name="Check" size="xs" color="green10" />
											<Text size="sm" color="sand10">
												{org.insuranceGroupsAccepted?.join(", ")}
											</Text>
										</Flex>
									)}
								{!isEditing &&
									(org.insuranceGroupsNotAccepted?.length ?? 0) > 0 && (
										<Flex gap="xs" align="center">
											<Icon name="X" size="xs" color="red10" />
											<Text size="sm" color="sand10">
												{org.insuranceGroupsNotAccepted?.join(", ")}
											</Text>
										</Flex>
									)}
								{/* Unknown insurance message when not editing and both lists empty */}
								{!isEditing &&
									(org.insuranceGroupsAccepted?.length ?? 0) === 0 &&
									(org.insuranceGroupsNotAccepted?.length ?? 0) === 0 && (
										<Text
											size="xs"
											color="sand9"
											style={{ fontStyle: "italic" }}
										>
											Insurance info unknown
										</Text>
									)}
							</Flex>
							{isEditing && canRemoveOrg && (
								<Button
									variant="ghost"
									size="xs"
									onPress={() => onRemoveOrg(org.id)}
									aria-label="Remove organization"
								>
									<Icon name="Trash" size="xs" color="red10" />
								</Button>
							)}
						</Flex>

						{/* Availability & specialty details (provider-level preferred, falls back to clinic) */}
						{!isEditing &&
							(org.providerAvailability ||
								org.availability ||
								org.providerSpecialtyDetails ||
								org.specialtyDetails) && (
								<Flex
									paddingHorizontal="sm"
									paddingVertical="xxs"
									gap="lg"
									align="center"
									style={{ paddingLeft: 24 }}
								>
									{(org.providerAvailability || org.availability) && (
										<Flex gap="xs" align="center">
											<Icon name="Clock" size="xs" color="accent10" />
											<Text size="sm" color="sand11">
												{org.providerAvailability ? "Provider" : "Clinic"}:{" "}
												{(
													org.providerAvailability || org.availability
												)?.toLocaleLowerCase()}
											</Text>
										</Flex>
									)}
									{(org.providerSpecialtyDetails || org.specialtyDetails) && (
										<Flex gap="xs" align="center">
											<Icon name="Stethoscope" size="xs" color="accent10" />
											<Text size="sm" color="sand11">
												{org.providerSpecialtyDetails ? "Provider" : "Clinic"}:{" "}
												{(
													org.providerSpecialtyDetails || org.specialtyDetails
												)?.toLocaleLowerCase()}
											</Text>
										</Flex>
									)}
								</Flex>
							)}

						{/* Insurance editing section */}
						{isEditing && (
							<Flex
								paddingHorizontal="sm"
								paddingVertical="xs"
								gap="lg"
								align="flex-start"
								style={{ paddingLeft: 24 }}
								column
							>
								{/* Insurances Accepted */}
								<Flex column gap="xs">
									<Flex gap="xs" align="center">
										<Icon name="Check" size="xs" color="green10" />
										<Text size="sm" weight="medium" color="sand11">
											Insurances Accepted
										</Text>
									</Flex>
									<Flex gap="xs" align="center" wrap>
										{(org.insuranceGroupsAccepted ?? []).map((ins) => (
											<Flex
												key={ins}
												paddingHorizontal="xs"
												paddingVertical="xxs"
												background="green3"
												borderRadius="sm"
												gap="xxs"
												align="center"
											>
												<Text size="sm" color="green11">
													{ins}
												</Text>
												<Button
													variant="ghost"
													size="xs"
													onPress={() => {
														const newList = (
															org.insuranceGroupsAccepted ?? []
														).filter((i) => i !== ins);
														onOrgChange(
															org.id,
															"insuranceGroupsAccepted",
															newList,
														);
													}}
													aria-label={`Remove ${ins}`}
												>
													<Icon name="X" size="xs" color="green11" />
												</Button>
											</Flex>
										))}
										<Select
											aria-label="Add accepted insurance"
											placeholder="Add..."
											size="xs"
											options={COLLOQUIAL_INSURANCE_NAMES.filter(
												(name) =>
													!(org.insuranceGroupsAccepted ?? []).includes(name),
											).map((name) => ({ value: name, label: name }))}
											onSelectionChange={(value) => {
												if (value) {
													const newList = [
														...(org.insuranceGroupsAccepted ?? []),
														value,
													];
													onOrgChange(
														org.id,
														"insuranceGroupsAccepted",
														newList,
													);
												}
											}}
											minWidth={80}
										/>
									</Flex>
								</Flex>

								{/* Insurances Not Accepted */}
								<Flex column gap="xs">
									<Flex gap="xs" align="center">
										<Icon name="X" size="xs" color="red10" />
										<Text size="sm" weight="medium" color="sand11">
											Insurances Not Accepted
										</Text>
									</Flex>
									<Flex gap="xs" align="center" wrap>
										{(org.insuranceGroupsNotAccepted ?? []).map((ins) => (
											<Flex
												key={ins}
												paddingHorizontal="xs"
												paddingVertical="xxs"
												background="red3"
												borderRadius="sm"
												gap="xxs"
												align="center"
											>
												<Text size="sm" color="red11">
													{ins}
												</Text>
												<Button
													variant="ghost"
													size="xs"
													onPress={() => {
														const newList = (
															org.insuranceGroupsNotAccepted ?? []
														).filter((i) => i !== ins);
														onOrgChange(
															org.id,
															"insuranceGroupsNotAccepted",
															newList,
														);
													}}
													aria-label={`Remove ${ins}`}
												>
													<Icon name="X" size="xs" color="red11" />
												</Button>
											</Flex>
										))}
										<Select
											aria-label="Add not accepted insurance"
											placeholder="Add..."
											size="xs"
											options={COLLOQUIAL_INSURANCE_NAMES.filter(
												(name) =>
													!(org.insuranceGroupsNotAccepted ?? []).includes(
														name,
													),
											).map((name) => ({ value: name, label: name }))}
											onSelectionChange={(value) => {
												if (value) {
													const newList = [
														...(org.insuranceGroupsNotAccepted ?? []),
														value,
													];
													onOrgChange(
														org.id,
														"insuranceGroupsNotAccepted",
														newList,
													);
												}
											}}
											minWidth={80}
										/>
									</Flex>
								</Flex>
							</Flex>
						)}

						{org.locations.length === 0 ? (
							<Flex
								paddingHorizontal="sm"
								paddingVertical="xs"
								style={{ paddingLeft: 24 }}
							>
								<Text size="sm" color="sand9" style={{ fontStyle: "italic" }}>
									No locations linked to provider
								</Text>
							</Flex>
						) : (
							org.locations.map((loc) => {
								const canRemoveLoc = org.locations.length > 1;
								const locPrefix = `loc:${org.id}:${loc.id}:`;
								const hasError =
									validationErrors[`${locPrefix}addressLine1`] ||
									validationErrors[`${locPrefix}city`];

								return (
									<Flex
										key={loc.id}
										className={locationRowClassName}
										paddingHorizontal="sm"
										paddingVertical="xs"
										gap="lg"
										align="center"
										background={hasError ? "red2" : "background"}
										style={{ paddingLeft: 24 }}
									>
										{isEditing ? (
											<Flex gap="md" align="center" wrap style={{ flex: 1 }}>
												<div
													className={editInputWrapperClassName}
													style={{ width: 160 }}
												>
													<Input
														size="xs"
														placeholder="Street"
														value={loc.addressLine1}
														onChange={(e) =>
															onLocationChange(
																org.id,
																loc.id,
																"addressLine1",
																e.target.value,
															)
														}
													/>
												</div>
												<div
													className={editInputWrapperClassName}
													style={{ width: 100 }}
												>
													<Input
														size="xs"
														placeholder="City"
														value={loc.city}
														onChange={(e) =>
															onLocationChange(
																org.id,
																loc.id,
																"city",
																e.target.value,
															)
														}
													/>
												</div>
												<div
													className={editInputWrapperClassName}
													style={{ width: 40 }}
												>
													<Input
														size="xs"
														placeholder="ST"
														value={loc.state}
														onChange={(e) =>
															onLocationChange(
																org.id,
																loc.id,
																"state",
																e.target.value,
															)
														}
													/>
												</div>
												<div
													className={editInputWrapperClassName}
													style={{ width: 70 }}
												>
													<Input
														size="xs"
														placeholder="Zip"
														value={loc.postalCode}
														onChange={(e) =>
															onLocationChange(
																org.id,
																loc.id,
																"postalCode",
																e.target.value,
															)
														}
													/>
												</div>
												<div
													className={editInputWrapperClassName}
													style={{ width: 110 }}
												>
													<Input
														size="xs"
														placeholder="Phone"
														value={loc.phone || ""}
														onChange={(e) =>
															onLocationChange(
																org.id,
																loc.id,
																"phone",
																e.target.value,
															)
														}
													/>
												</div>
												<div
													className={editInputWrapperClassName}
													style={{ width: 110 }}
												>
													<Input
														size="xs"
														placeholder="Fax"
														value={loc.fax || ""}
														onChange={(e) =>
															onLocationChange(
																org.id,
																loc.id,
																"fax",
																e.target.value,
															)
														}
													/>
												</div>
											</Flex>
										) : (
											<Flex gap="lg" align="center" style={{ flex: 1 }}>
												<Flex gap="xs" align="center">
													<Icon name="MapPin" size="xs" color="sand8" />
													<Text size="sm" color="sand12">
														{loc.addressLine1 || "—"}
													</Text>
												</Flex>
												<Text size="sm" color="sand11">
													{[loc.city, loc.state, loc.postalCode]
														.filter(Boolean)
														.join(", ") || "—"}
												</Text>
												{loc.phone && (
													<Flex gap="xs" align="center">
														<Icon name="Phone" size="xs" color="sand8" />
														<Text size="sm" color="sand11">
															{loc.phone}
														</Text>
													</Flex>
												)}
												{loc.fax && (
													<Flex gap="xs" align="center">
														<Icon name="FileText" size="xs" color="sand8" />
														<Text size="sm" color="sand11">
															{loc.fax}
														</Text>
													</Flex>
												)}
											</Flex>
										)}
										<Flex gap="xs" align="center" shrink={0}>
											{!isEditing && (
												<WithTooltip content="Copy">
													<Button
														variant="ghost"
														size="xs"
														onPress={() => handleCopyLocation(org, loc)}
														aria-label="Copy location"
													>
														<Icon name="Copy" size="xs" />
													</Button>
												</WithTooltip>
											)}
											{isEditing && canRemoveLoc && (
												<WithTooltip content="Remove">
													<Button
														variant="ghost"
														size="xs"
														onPress={() => onRemoveLocation(org.id, loc.id)}
														aria-label="Remove location"
													>
														<Icon name="Trash" size="xs" color="red10" />
													</Button>
												</WithTooltip>
											)}
										</Flex>
									</Flex>
								);
							})
						)}
					</Flex>
				);
			})}
		</Flex>
	);
}
